import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Mail, ArrowLeft, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) { toast.error('Please enter your email'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-navy-950 flex items-center justify-center px-6 py-12">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,rgba(0,212,255,0.1)_0%,transparent_60%)]" />

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md relative">
        <Link to="/" className="flex justify-center mb-8">
          <img
            src="https://customer-assets.emergentagent.com/job_7d9bc2e5-ff38-4e06-b101-9f0eb41c2616/artifacts/w350as9h_LOGOvrn-02.png"
            alt="VerbNotes"
            className="h-16"
          />
        </Link>

        <div className="glass rounded-2xl p-8">
          {sent ? (
            <div className="text-center space-y-4" data-testid="forgot-password-success">
              <div className="w-14 h-14 mx-auto rounded-full bg-cyan/15 flex items-center justify-center">
                <Mail className="text-cyan" size={28} />
              </div>
              <h1 className="font-heading text-2xl text-white">Check your email</h1>
              <p className="text-slate-400 text-sm leading-relaxed">
                If an account with <span className="text-white font-medium">{email}</span> exists, we sent a password reset link. It expires in 1 hour.
              </p>
              <p className="text-slate-500 text-xs">
                Don't see it? Check your spam folder or contact your administrator.
              </p>
              <Link to="/login">
                <Button variant="outline" className="mt-4 border-white/10 text-slate-300 hover:text-white gap-2" data-testid="back-to-login-btn">
                  <ArrowLeft size={16} /> Back to login
                </Button>
              </Link>
            </div>
          ) : (
            <>
              <h1 className="font-heading text-3xl text-white text-center mb-2">Reset password</h1>
              <p className="text-slate-400 text-center mb-6 text-sm">
                Enter your email and we'll send you a link to reset your password.
              </p>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-300">Email address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="bg-navy-900 border-white/10 text-white placeholder:text-slate-500"
                    autoFocus
                    data-testid="forgot-email-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-cyan text-navy-950 hover:bg-cyan-300 rounded-full py-6 font-semibold shadow-[0_0_20px_rgba(0,212,255,0.3)]"
                  data-testid="forgot-submit-btn"
                >
                  {loading ? <Loader2 className="animate-spin" size={18} /> : 'Send reset link'}
                </Button>
              </form>

              <p className="text-center mt-6">
                <Link to="/login" className="text-cyan text-sm hover:underline flex items-center justify-center gap-1" data-testid="forgot-back-link">
                  <ArrowLeft size={14} /> Back to login
                </Link>
              </p>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}
